import {
  ApiChartAttribute,
  ApiChartRankDrection,
  ApiDimension,
  ApiMetric,
  ApiOperator,
} from "@incendium/api";
import { Backspace } from "@mui/icons-material";
import {
  Box,
  Button,
  Portal,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { formatDimension } from "features/analytics";
import { useDimensionName } from "features/analytics/hooks/useDimensionName";
import useDimensionQueryParams from "features/analytics/hooks/useDimensionQueryParams";
import { ContentExplorer, useExplorerContext } from "features/explorer";
import { ExplorerProvider } from "features/explorer";
import { TExploreConfig } from "features/explorer/types";
import useAppendSearchState from "Hooks/useAppendSearchState";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";

function ContentPerformancePageInner({ metrics, deepDiveAttribute }) {
  const { type, setType } = useExplorerContext<ContentTab>();
  const [prevType, setPrevType] = useState<ContentTab>("top content");
  const dimensionQueryParams = useDimensionQueryParams();
  const history = useHistory();
  const dimensionToName = useDimensionName();

  useEffect(() => {
    if (Object.keys(dimensionQueryParams).length > 0) {
      if (type !== "deepdive") {
        setPrevType(type);
      }

      setType("deepdive");
    }
  }, [dimensionQueryParams, setType, type]);

  const onGoBack = useCallback(() => {
    let searchParams = new URLSearchParams(window.location.search);
    searchParams.set("expl", prevType || "top content");
    Object.entries(dimensionQueryParams).forEach(([k, v]) => {
      searchParams.delete((deepDiveAttribute?.key as ApiDimension) || "");
    });
    const updatedSearchString = searchParams.toString();
    history.push({ search: updatedSearchString });
  }, [deepDiveAttribute, history, dimensionQueryParams, prevType]);

  return (
    <>
      <Portal container={() => document.getElementById("pageTitle")}>
        <Typography variant="h1">Content Performance Analysis</Typography>
        {deepDiveAttribute?.key && (
          <Stack mt={2} direction={"row"} spacing={2} alignItems={"center"}>
            <Button
              size="small"
              startIcon={<Backspace fontSize="small" />}
              onClick={onGoBack}
            >
              Go Back
            </Button>

            <Typography variant="subtitle1">
              Deep Dive:{" "}
              <Typography
                variant="subtitle1"
                display={"inline"}
                color={"secondary"}
              >
                {dimensionToName(deepDiveAttribute.key as ApiDimension)} -{" "}
                {formatDimension(
                  deepDiveAttribute.key as ApiDimension,
                  deepDiveAttribute.value || "",
                )}
              </Typography>
            </Typography>
          </Stack>
        )}
      </Portal>
      <ContentExplorer limit={5000} metrics={metrics} dontIncludeEmpty />
    </>
  );
}

type ContentTab = "top content" | "content to improve" | "explore" | "deepdive";
type MetricStyle = "session" | "pageview";

const PAGEVIEW_ONLY_DIMENSIONS = [
  ApiDimension.DIMENSION_LOCATION,
  ApiDimension.DIMENSION_LOCATION_PAGE_TYPE,
  ApiDimension.DIMENSION_PATH,
];

function AnalyseBySwitch({
  metricStyle,
  setMetricStyle,
}: {
  metricStyle: MetricStyle;
  setMetricStyle: (v: MetricStyle) => void;
}) {
  const { dimension } = useExplorerContext<ContentTab>();
  const isSessionDisabled = PAGEVIEW_ONLY_DIMENSIONS.includes(dimension);
  return (
    <Box mt={2}>
      <ToggleButtonGroup
        size="small"
        fullWidth
        value={metricStyle}
        exclusive
        color="secondary"
        onChange={(e, v) => {
          if (v === "session" || v === "pageview")
            setMetricStyle(v as MetricStyle);
        }}
      >
        <ToggleButton value={"session"} disabled={isSessionDisabled}>
          Session
        </ToggleButton>
        <ToggleButton value={"pageview"}>Pageview</ToggleButton>
      </ToggleButtonGroup>
    </Box>
  );
}

function ContentPerformancePage() {
  const dimensionQueryParams = useDimensionQueryParams();
  const [deepDiveAttribute, setDeepDiveAttribute] =
    useState<ApiChartAttribute | null>(null);
  const [metricStyle, setMetricStyle] = useAppendSearchState<
    "session" | "pageview"
  >("mes", "session");

  const handleAnalayseBy = useCallback(
    (d) => {
      const sessionDimensions = [
        ApiDimension.DIMENSION_SESSION_LANDING_PAGE_LOCATION,
        ApiDimension.DIMENSION_SESSION_LANDING_PAGE_TYPE,
        ApiDimension.DIMENSION_SESSION_LANDING_PAGE_PATH,
      ];
      if (sessionDimensions.includes(d)) {
        setMetricStyle("session");
      } else {
        setMetricStyle("pageview");
      }
    },
    [setMetricStyle],
  );

  const analyseBySwitch = useMemo(
    () => (
      <AnalyseBySwitch
        metricStyle={metricStyle ?? "session"}
        setMetricStyle={setMetricStyle}
      />
    ),
    [metricStyle, setMetricStyle],
  );

  const config: TExploreConfig<ContentTab> = useMemo(() => {
    return {
      deepdive: {
        hidden: !Boolean(deepDiveAttribute),
        title: `Content Analysis - Deep Dive`,
        desc: "Use this deep dive to surface actionable insights",
        ranking: [
          {
            defaultRank: 0.0001,
          },
        ],
        attributes: deepDiveAttribute ? [deepDiveAttribute] : [],
        analyseBy: {
          description: "Select how to split content",
          onChange: handleAnalayseBy,
          children: analyseBySwitch,
          grouping: [
            {
              option: "landing Pages",
              dimensions: [
                ApiDimension.DIMENSION_BROWSER,
                ApiDimension.DIMENSION_BROWSER_WITH_VERSION,
                ApiDimension.DIMENSION_DEVICE,
                ApiDimension.DIMENSION_CHANNEL,
                ApiDimension.DIMENSION_SESSION_AUDIENCE,
              ],
            },
            {
              option: "Last Click Attribution",
              dimensions: [
                ApiDimension.DIMENSION_CHANNEL,
                ApiDimension.DIMENSION_SOURCE,
                ApiDimension.DIMENSION_MEDIUM,
                ApiDimension.DIMENSION_CAMPAIGN,
                ApiDimension.DIMENSION_CONTENT,
                ApiDimension.DIMENSION_TERM,
              ],
            },
          ],
        },
      },
      "top content": {
        hidden: Boolean(deepDiveAttribute),
        title: "Top Content",
        desc: "These are the most visited and top-performing pages. Use them for insights or prioritize them in your marketing strategy and site hierarchy.",
        ranking: [
          {
            type: "top",
            ranks: [
              {
                metric: ApiMetric.METRIC_PAGEVIEWS_COUNT,
                direction: ApiChartRankDrection.ASC,
                weight: 0.1,
              },
              {
                metric: ApiMetric.METRIC_PAGE_MACRO_CONVERSIONS_COUNT,
                direction: ApiChartRankDrection.ASC,
                weight: 0.1,
              },
              ...(metricStyle === "pageview"
                ? [
                    {
                      metric: ApiMetric.METRIC_PAGE_MICRO_CONVERSIONS_COUNT,
                      direction: ApiChartRankDrection.ASC,
                      weight: 0.1,
                    },
                  ]
                : []),
              {
                metric: ApiMetric.METRIC_EFFECTIVE_PAGEVIEW_RATE,
                direction: ApiChartRankDrection.ASC,
                weight: 0.1,
              },
              {
                metric: ApiMetric.METRIC_SESSION_ADD_TO_CART_CLICK_RATE,
                direction: ApiChartRankDrection.ASC,
                weight: 0.1,
              },
              ...(metricStyle === "pageview"
                ? [
                    {
                      metric: ApiMetric.METRIC_AVERAGE_CWV_PERFORMANCE_SCORE,
                      direction: ApiChartRankDrection.ASC,
                      weight: 0.1,
                    },
                  ]
                : []),
            ],
          },
        ],
        analyseBy: {
          description: "Select how to split content",
          onChange: handleAnalayseBy,
          children: analyseBySwitch,
          grouping: [
            {
              option: "landing Pages",
              dimensions:
                metricStyle === "session"
                  ? [
                      ApiDimension.DIMENSION_SESSION_LANDING_PAGE_LOCATION,
                      ApiDimension.DIMENSION_SESSION_LANDING_PAGE_TYPE,
                      ApiDimension.DIMENSION_SESSION_LANDING_PAGE_PATH,
                    ]
                  : [
                      ApiDimension.DIMENSION_LANDING_PAGE_LOCATION,
                      ApiDimension.DIMENSION_LANDING_PAGE_TYPE,
                      ApiDimension.DIMENSION_LANDING_PAGE_PATH,
                    ],
            },
            {
              option: "All Pages",
              dimensions: [
                ApiDimension.DIMENSION_LOCATION,
                ApiDimension.DIMENSION_LOCATION_PAGE_TYPE,
                ApiDimension.DIMENSION_PATH,
              ],
            },
          ],
        },
        default: [
          ApiMetric.METRIC_PAGEVIEWS_COUNT,
          ApiMetric.METRIC_PAGE_MACRO_CONVERSIONS_COUNT,
        ],
        having: {
          description: "Exclude content based on rank weights",
          havings: [
            {
              label: "Min No. of Pageviews",
              metric: ApiMetric.METRIC_PAGEVIEWS_COUNT,
              operator: ApiOperator.IS_GREATER_THAN_OR_EQUAL,
              value: undefined,
            },
            {
              label: "Max No. of Pageviews",
              metric: ApiMetric.METRIC_PAGEVIEWS_COUNT,
              operator: ApiOperator.IS_LESS_THAN_OR_EQUAL,
              value: undefined,
            },
            {
              label: "Min No. of Conversions",
              metric: ApiMetric.METRIC_PAGE_MACRO_CONVERSIONS_COUNT,
              operator: ApiOperator.IS_GREATER_THAN_OR_EQUAL,
              value: undefined,
            },
            {
              label: "Max No. of Conversions",
              metric: ApiMetric.METRIC_PAGE_MACRO_CONVERSIONS_COUNT,
              operator: ApiOperator.IS_LESS_THAN_OR_EQUAL,
              value: undefined,
            },
            ...(metricStyle === "pageview"
              ? [
                  {
                    label: "Min No. of Interactions",
                    metric: ApiMetric.METRIC_PAGE_MICRO_CONVERSIONS_COUNT,
                    operator: ApiOperator.IS_GREATER_THAN_OR_EQUAL,
                    value: undefined,
                  },
                  {
                    label: "Max No. of Interactions",
                    metric: ApiMetric.METRIC_PAGE_MICRO_CONVERSIONS_COUNT,
                    operator: ApiOperator.IS_LESS_THAN_OR_EQUAL,
                    value: undefined,
                  },
                ]
              : []),
            {
              label: "Min Effective Rate",
              metric: ApiMetric.METRIC_EFFECTIVE_PAGEVIEW_RATE,
              operator: ApiOperator.IS_GREATER_THAN_OR_EQUAL,
              value: undefined,
            },
            {
              label: "Max Effective Rate",
              metric: ApiMetric.METRIC_EFFECTIVE_PAGEVIEW_RATE,
              operator: ApiOperator.IS_LESS_THAN_OR_EQUAL,
              value: undefined,
            },
            {
              label: "Min Add to Cart Rate",
              metric: ApiMetric.METRIC_SESSION_ADD_TO_CART_CLICK_RATE,
              operator: ApiOperator.IS_GREATER_THAN_OR_EQUAL,
              value: undefined,
            },
            {
              label: "Max Add to Cart Rate",
              metric: ApiMetric.METRIC_SESSION_ADD_TO_CART_CLICK_RATE,
              operator: ApiOperator.IS_LESS_THAN_OR_EQUAL,
              value: undefined,
            },
          ],
        },
      },
      "content to improve": {
        hidden: Boolean(deepDiveAttribute),
        title: "Content to Improve",
        desc: "These are your worst performing pages; the most visited, but poorest performing in terms of conversion on engagement. Analyse the pages and consider changes to copy, visuals, pricing or perhaps review the traffic sources as they may be mismatched.",
        ranking: [
          {
            type: "bottom",
            ranks: [
              {
                metric: ApiMetric.METRIC_PAGEVIEWS_COUNT,
                direction: ApiChartRankDrection.ASC,
                weight: 0.1,
              },
              {
                metric: ApiMetric.METRIC_PAGE_MACRO_CONVERSIONS_COUNT,
                direction: ApiChartRankDrection.DESC,
                weight: 0.1,
              },
              ...(metricStyle === "pageview"
                ? [
                    {
                      metric: ApiMetric.METRIC_PAGE_MICRO_CONVERSIONS_COUNT,
                      direction: ApiChartRankDrection.DESC,
                      weight: 0.1,
                    },
                  ]
                : []),
              {
                metric: ApiMetric.METRIC_EFFECTIVE_PAGEVIEW_RATE,
                direction: ApiChartRankDrection.DESC,
                weight: 0.1,
              },
              {
                metric: ApiMetric.METRIC_SESSION_ADD_TO_CART_CLICK_RATE,
                direction: ApiChartRankDrection.DESC,
                weight: 0.1,
              },
              ...(metricStyle === "pageview"
                ? [
                    {
                      metric: ApiMetric.METRIC_AVERAGE_CWV_PERFORMANCE_SCORE,
                      direction: ApiChartRankDrection.ASC,
                      weight: 0.1,
                    },
                  ]
                : []),
            ],
          },
        ],

        analyseBy: {
          description: "Select how to split content",
          onChange: handleAnalayseBy,
          children: analyseBySwitch,
          grouping: [
            {
              option: "landing Pages",
              dimensions:
                metricStyle === "session"
                  ? [
                      ApiDimension.DIMENSION_SESSION_LANDING_PAGE_LOCATION,
                      ApiDimension.DIMENSION_SESSION_LANDING_PAGE_TYPE,
                      ApiDimension.DIMENSION_SESSION_LANDING_PAGE_PATH,
                    ]
                  : [
                      ApiDimension.DIMENSION_LANDING_PAGE_LOCATION,
                      ApiDimension.DIMENSION_LANDING_PAGE_TYPE,
                      ApiDimension.DIMENSION_LANDING_PAGE_PATH,
                    ],
            },
            {
              option: "All Pages",
              dimensions: [
                ApiDimension.DIMENSION_LOCATION,
                ApiDimension.DIMENSION_LOCATION_PAGE_TYPE,
                ApiDimension.DIMENSION_PATH,
              ],
            },
          ],
        },
        default: [
          ApiMetric.METRIC_PAGEVIEWS_COUNT,
          ApiMetric.METRIC_PAGE_MACRO_CONVERSIONS_COUNT,
        ],
        having: {
          description: "Exclude content based on rank weights",
          havings: [
            {
              label: "Min No. of Pageviews",
              metric: ApiMetric.METRIC_PAGEVIEWS_COUNT,
              operator: ApiOperator.IS_GREATER_THAN_OR_EQUAL,
              value: undefined,
            },
            {
              label: "Max No. of Pageviews",
              metric: ApiMetric.METRIC_PAGEVIEWS_COUNT,
              operator: ApiOperator.IS_LESS_THAN_OR_EQUAL,
              value: undefined,
            },
            {
              label: "Min No. of Conversions",
              metric: ApiMetric.METRIC_PAGE_MACRO_CONVERSIONS_COUNT,
              operator: ApiOperator.IS_GREATER_THAN_OR_EQUAL,
              value: undefined,
            },
            {
              label: "Max No. of Conversions",
              metric: ApiMetric.METRIC_PAGE_MACRO_CONVERSIONS_COUNT,
              operator: ApiOperator.IS_LESS_THAN_OR_EQUAL,
              value: undefined,
            },
            ...(metricStyle === "pageview"
              ? [
                  {
                    label: "Min No. of Interactions",
                    metric: ApiMetric.METRIC_PAGE_MICRO_CONVERSIONS_COUNT,
                    operator: ApiOperator.IS_GREATER_THAN_OR_EQUAL,
                    value: undefined,
                  },
                  {
                    label: "Max No. of Interactions",
                    metric: ApiMetric.METRIC_PAGE_MICRO_CONVERSIONS_COUNT,
                    operator: ApiOperator.IS_LESS_THAN_OR_EQUAL,
                    value: undefined,
                  },
                ]
              : []),
            {
              label: "Min Effective Rate",
              metric: ApiMetric.METRIC_EFFECTIVE_PAGEVIEW_RATE,
              operator: ApiOperator.IS_GREATER_THAN_OR_EQUAL,
              value: undefined,
            },
            {
              label: "Max Effective Rate",
              metric: ApiMetric.METRIC_EFFECTIVE_PAGEVIEW_RATE,
              operator: ApiOperator.IS_LESS_THAN_OR_EQUAL,
              value: undefined,
            },
            {
              label: "Min Add to Cart Rate",
              metric: ApiMetric.METRIC_SESSION_ADD_TO_CART_CLICK_RATE,
              operator: ApiOperator.IS_GREATER_THAN_OR_EQUAL,
              value: undefined,
            },
            {
              label: "Max Add to Cart Rate",
              metric: ApiMetric.METRIC_SESSION_ADD_TO_CART_CLICK_RATE,
              operator: ApiOperator.IS_LESS_THAN_OR_EQUAL,
              value: undefined,
            },
          ],
        },
      },
      explore: {
        hidden: Boolean(deepDiveAttribute),
        title: "Content Analysis",
        desc: "Use this tool to diagnose content issues. Click to deep dive and segment further.",
        ranking: [
          {
            ranks: [
              {
                metric: ApiMetric.METRIC_PAGEVIEWS_COUNT,
                direction: ApiChartRankDrection.ASC,
                weight: 0.1,
              },
              {
                metric: ApiMetric.METRIC_PAGEVIEWS_COUNT,
                direction: ApiChartRankDrection.DESC,
                weight: 0.1,
              },
              {
                metric: ApiMetric.METRIC_PAGE_MACRO_CONVERSIONS_COUNT,
                direction: ApiChartRankDrection.ASC,
                weight: 0.1,
              },
              {
                metric: ApiMetric.METRIC_PAGE_MACRO_CONVERSIONS_COUNT,
                direction: ApiChartRankDrection.DESC,
                weight: 0.1,
              },
              ...(metricStyle === "pageview"
                ? [
                    {
                      metric: ApiMetric.METRIC_PAGE_MICRO_CONVERSIONS_COUNT,
                      direction: ApiChartRankDrection.ASC,
                      weight: 0.1,
                    },
                    {
                      metric: ApiMetric.METRIC_PAGE_MICRO_CONVERSIONS_COUNT,
                      direction: ApiChartRankDrection.DESC,
                      weight: 0.1,
                    },
                  ]
                : []),
              {
                metric: ApiMetric.METRIC_EFFECTIVE_PAGEVIEW_RATE,
                direction: ApiChartRankDrection.ASC,
                weight: 0.1,
              },
              {
                metric: ApiMetric.METRIC_EFFECTIVE_PAGEVIEW_RATE,
                direction: ApiChartRankDrection.DESC,
                weight: 0.1,
              },
              {
                metric: ApiMetric.METRIC_SESSION_ADD_TO_CART_CLICK_RATE,
                direction: ApiChartRankDrection.ASC,
                weight: 0.1,
              },
              {
                metric: ApiMetric.METRIC_SESSION_ADD_TO_CART_CLICK_RATE,
                direction: ApiChartRankDrection.DESC,
                weight: 0.1,
              },
              ...(metricStyle === "pageview"
                ? [
                    {
                      metric: ApiMetric.METRIC_AVERAGE_CWV_PERFORMANCE_SCORE,
                      direction: ApiChartRankDrection.ASC,
                      weight: 0.1,
                    },
                    {
                      metric: ApiMetric.METRIC_AVERAGE_CWV_PERFORMANCE_SCORE,
                      direction: ApiChartRankDrection.DESC,
                      weight: 0.1,
                    },
                  ]
                : []),
            ],
          },
        ],
        type: "top",
        analyseBy: {
          description: "Select how to split content",
          onChange: handleAnalayseBy,
          children: analyseBySwitch,
          grouping: [
            {
              option: "landing Pages",
              dimensions:
                metricStyle === "session"
                  ? [
                      ApiDimension.DIMENSION_SESSION_LANDING_PAGE_LOCATION,
                      ApiDimension.DIMENSION_SESSION_LANDING_PAGE_TYPE,
                      ApiDimension.DIMENSION_SESSION_LANDING_PAGE_PATH,
                    ]
                  : [
                      ApiDimension.DIMENSION_LANDING_PAGE_LOCATION,
                      ApiDimension.DIMENSION_LANDING_PAGE_TYPE,
                      ApiDimension.DIMENSION_LANDING_PAGE_PATH,
                    ],
            },
            {
              option: "All Pages",
              dimensions: [
                ApiDimension.DIMENSION_LOCATION,
                ApiDimension.DIMENSION_LOCATION_PAGE_TYPE,
                ApiDimension.DIMENSION_PATH,
              ],
            },
          ],
        },
        default: [],
        having: {
          description: "Exclude content based on rank weights",
          havings: [
            {
              label: "Min No. of Pageviews",
              metric: ApiMetric.METRIC_PAGEVIEWS_COUNT,
              operator: ApiOperator.IS_GREATER_THAN_OR_EQUAL,
              value: undefined,
            },
            {
              label: "Max No. of Pageviews",
              metric: ApiMetric.METRIC_PAGEVIEWS_COUNT,
              operator: ApiOperator.IS_LESS_THAN_OR_EQUAL,
              value: undefined,
            },
            {
              label: "Min No. of Conversions",
              metric: ApiMetric.METRIC_PAGE_MACRO_CONVERSIONS_COUNT,
              operator: ApiOperator.IS_GREATER_THAN_OR_EQUAL,
              value: undefined,
            },
            {
              label: "Max No. of Conversions",
              metric: ApiMetric.METRIC_PAGE_MACRO_CONVERSIONS_COUNT,
              operator: ApiOperator.IS_LESS_THAN_OR_EQUAL,
              value: undefined,
            },
            ...(metricStyle === "pageview"
              ? [
                  {
                    label: "Min No. of Interactions",
                    metric: ApiMetric.METRIC_PAGE_MICRO_CONVERSIONS_COUNT,
                    operator: ApiOperator.IS_GREATER_THAN_OR_EQUAL,
                    value: undefined,
                  },
                  {
                    label: "Max No. of Interactions",
                    metric: ApiMetric.METRIC_PAGE_MICRO_CONVERSIONS_COUNT,
                    operator: ApiOperator.IS_LESS_THAN_OR_EQUAL,
                    value: undefined,
                  },
                ]
              : []),
            {
              label: "Min Effective Rate",
              metric: ApiMetric.METRIC_EFFECTIVE_PAGEVIEW_RATE,
              operator: ApiOperator.IS_GREATER_THAN_OR_EQUAL,
              value: undefined,
            },
            {
              label: "Max Effective Rate",
              metric: ApiMetric.METRIC_EFFECTIVE_PAGEVIEW_RATE,
              operator: ApiOperator.IS_LESS_THAN_OR_EQUAL,
              value: undefined,
            },
            {
              label: "Min Add to Cart Rate",
              metric: ApiMetric.METRIC_SESSION_ADD_TO_CART_CLICK_RATE,
              operator: ApiOperator.IS_GREATER_THAN_OR_EQUAL,
              value: undefined,
            },
            {
              label: "Max Add to Cart Rate",
              metric: ApiMetric.METRIC_SESSION_ADD_TO_CART_CLICK_RATE,
              operator: ApiOperator.IS_LESS_THAN_OR_EQUAL,
              value: undefined,
            },
          ],
        },
      },
    };
  }, [deepDiveAttribute, analyseBySwitch, handleAnalayseBy, metricStyle]);

  useEffect(() => {
    Object.entries(dimensionQueryParams).forEach(([k, v]) => {
      setDeepDiveAttribute({
        key: k,
        value: v,
      });
    });
  }, [dimensionQueryParams]);

  const metrics = useMemo(() => {
    if (metricStyle === "session") {
      return [
        ApiMetric.METRIC_SESSIONS_COUNT,
        ApiMetric.METRIC_EFFECTIVE_SESSION_RATE,
        ApiMetric.METRIC_AVERAGE_TIME_ON_PAGE_DURING_SESSION,
        ApiMetric.METRIC_AVERAGE_SCROLL_PERCENTAGE_DURING_SESSION,
        ApiMetric.METRIC_MACRO_CONVERSION_RATE,
        ApiMetric.METRIC_SESSION_ADD_TO_CART_CLICK_RATE,
        ApiMetric.METRIC_REVENUE,
        ApiMetric.METRIC_RANK,
      ];
    }
    return [
      ApiMetric.METRIC_PAGEVIEWS_COUNT,
      ApiMetric.METRIC_EFFECTIVE_PAGEVIEW_RATE,
      ApiMetric.METRIC_AVERAGE_TIME_ON_PAGE,
      ApiMetric.METRIC_AVERAGE_SCROLL_PERCENTAGE,
      ApiMetric.METRIC_PAGE_MACRO_CONVERSION_RATE,
      ApiMetric.METRIC_PAGE_MICRO_CONVERSION_RATE,
      ApiMetric.METRIC_PAGE_ADD_TO_CART_CLICK_RATE,
      ApiMetric.METRIC_REVENUE,
      ApiMetric.METRIC_AVERAGE_CWV_PERFORMANCE_SCORE,
      ApiMetric.METRIC_RANK,
    ];
  }, [metricStyle]);

  return (
    <>
      <ExplorerProvider configs={config} type={"top content"}>
        <ContentPerformancePageInner
          metrics={metrics}
          deepDiveAttribute={deepDiveAttribute}
        />
      </ExplorerProvider>
    </>
  );
}

export default ContentPerformancePage;
